/**
 * Reordenar la siembra antes de tirar el torneo.
 *
 * SE ARRASTRA CON EVENTOS DE PUNTERO Y NO CON EL «drag and drop» DE HTML.
 * El de HTML no existe en un celular: `dragstart` sencillamente no ocurre al
 * tocar. Como este torneo se arma tanto desde la compu como desde el
 * teléfono, arrastrar tiene que funcionar en los dos, y `pointerdown` /
 * `pointermove` / `pointerup` son los mismos eventos para el dedo, el mouse y
 * el lápiz.
 *
 * Y ADEMÁS HAY BOTONES DE SUBIR Y BAJAR. No son un adorno ni un plan B:
 * arrastrar es imposible con el teclado y muy difícil con un lector de
 * pantalla o con la mano temblorosa. Cualquier cosa que solo se pueda hacer
 * arrastrando es una cosa que alguna gente no puede hacer.
 *
 * Nada de esto toca el servidor hasta que se aprieta «Guardar el orden»: se
 * mueve, se mira cómo queda, y recién entonces se manda. Guardar en cada
 * movimiento serían veinte llamadas para acomodar ocho nombres.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TournamentEntry } from '@tennisup/contracts';

import { Avatar } from './Avatar';
import { assetUrl } from '../lib/api';
import { nombreParaLeer } from '../lib/nombreParaLeer';

interface SeedListProps {
  entries: readonly TournamentEntry[];
  onSave: (entryIdsInOrder: string[]) => Promise<void>;
  onRemove?: (entryId: string) => Promise<void>;
  disabled?: boolean;
}

/** Mueve el elemento de `from` a `to` y devuelve un arreglo nuevo. */
export function move<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return [...list];
  }
  const copia = [...list];
  const [sacado] = copia.splice(from, 1);
  if (sacado !== undefined) copia.splice(to, 0, sacado);
  return copia;
}

export function SeedList({ entries, onSave, onRemove, disabled = false }: SeedListProps): JSX.Element {
  const { t } = useTranslation();
  const [order, setOrder] = useState<TournamentEntry[]>([...entries]);
  const [dragging, setDragging] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const listRef = useRef<HTMLOListElement>(null);

  /**
   * Si CAMBIÓ QUIÉN está inscrito —alguien se inscribió o se borró—, la lista
   * de arriba manda y el orden en pantalla se descarta.
   *
   * Se compara el conjunto de ids y no el orden: si comparara el orden, cada
   * vez que alguien arrastra un nombre la lista se «arreglaría» sola a la
   * versión del servidor y el arrastre no serviría de nada. Es el patrón de
   * React para ajustar estado cuando cambian las props, y por eso el
   * `setState` va acá y no en un efecto.
   */
  const idsDelServidor = [...entries].map((e) => e.id).sort().join('|');
  const [idsVistos, setIdsVistos] = useState(idsDelServidor);
  if (idsDelServidor !== idsVistos) {
    setIdsVistos(idsDelServidor);
    setOrder([...entries]);
  }

  const cambiado = order.map((e) => e.id).join('|') !== entries.map((e) => e.id).join('|');

  function indiceEn(clientY: number): number {
    // Se pregunta por la posición de cada fila en vez de calcularla: las filas
    // no miden todas lo mismo (un nombre largo ocupa dos líneas) y suponer
    // que sí es lo que hace que el arrastre «salte» al final de la lista.
    const filas = Array.from(listRef.current?.querySelectorAll('li') ?? []);
    for (let i = 0; i < filas.length; i += 1) {
      const r = filas[i]!.getBoundingClientRect();
      if (clientY < r.top + r.height / 2) return i;
    }
    return filas.length - 1;
  }

  function onPointerDown(event: React.PointerEvent<HTMLButtonElement>, id: string): void {
    if (disabled || saving) return;
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    setDragging(id);
  }

  function onPointerMove(event: React.PointerEvent<HTMLButtonElement>): void {
    if (dragging === null) return;
    const desde = order.findIndex((e) => e.id === dragging);
    const hasta = indiceEn(event.clientY);
    if (desde !== -1 && hasta !== -1 && desde !== hasta) setOrder((actual) => move(actual, desde, hasta));
  }

  function onPointerUp(event: React.PointerEvent<HTMLButtonElement>): void {
    if (dragging === null) return;
    (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
    setDragging(null);
  }

  function mover(index: number, delta: -1 | 1): void {
    setOrder((actual) => move(actual, index, index + delta));
  }

  async function guardar(): Promise<void> {
    setSaving(true);
    try {
      await onSave(order.map((e) => e.id));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="seed-list">
      <p className="help">{t('seeding.help')}</p>

      <ol className="seed-rows" ref={listRef}>
        {order.map((entry, index) => (
          <li key={entry.id} className={dragging === entry.id ? 'seed-row dragging' : 'seed-row'}>
            <span className="seed-number" aria-hidden="true">
              {index + 1}
            </span>

            <button
              type="button"
              className="seed-handle"
              // `touch-action: none` en el CSS: sin eso, el navegador se queda
              // con el gesto para desplazar la página y el arrastre nunca
              // llega.
              aria-label={t('seeding.dragHandle', { name: nombreParaLeer(entry) })}
              disabled={disabled || saving}
              onPointerDown={(e) => onPointerDown(e, entry.id)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <span aria-hidden="true">⠿</span>
            </button>

            <Avatar url={assetUrl(entry.avatarPath)} name={entry.displayName} size={32} />
            <span className="seed-name">{entry.displayName}</span>

            <span className="seed-buttons">
              <button
                type="button"
                className="link"
                disabled={disabled || saving || index === 0}
                aria-label={t('seeding.up', { name: nombreParaLeer(entry) })}
                onClick={() => mover(index, -1)}
              >
                <span aria-hidden="true">↑</span>
              </button>
              <button
                type="button"
                className="link"
                disabled={disabled || saving || index === order.length - 1}
                aria-label={t('seeding.down', { name: nombreParaLeer(entry) })}
                onClick={() => mover(index, 1)}
              >
                <span aria-hidden="true">↓</span>
              </button>
              {onRemove !== undefined && (
                <button
                  type="button"
                  className="link"
                  disabled={disabled || saving}
                  aria-label={t('seeding.remove', { name: nombreParaLeer(entry) })}
                  onClick={() => void onRemove(entry.id)}
                >
                  {t('members.remove')}
                </button>
              )}
            </span>
          </li>
        ))}
      </ol>

      <div className="button-row">
        <button type="button" onClick={() => void guardar()} disabled={disabled || saving || !cambiado}>
          {saving ? t('common.sending') : t('seeding.save')}
        </button>
        {cambiado && (
          <button
            type="button"
            className="link"
            disabled={saving}
            onClick={() => setOrder([...entries])}
          >
            {t('common.cancel')}
          </button>
        )}
      </div>
    </div>
  );
}
